import { MapPin, Phone, Globe, ArrowUpRight, FileText, Presentation, Wrench } from "lucide-react";
import { IMAGES, PHONES, WEBSITE_LABEL, WEBSITE_URL, BROCHURE_URL } from "@/data/site";
import { useI18n } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { Parallax } from "@/components/site/Parallax";
import { Button } from "@/components/ui/button";

export const Venue = () => {
  const { t } = useI18n();
  const v = t.venue;
  return (
    <section id="contact" className="scroll-mt-24 bg-background">
      <div className="container-site section-pad py-20 md:py-24">
        <div className="grid items-stretch gap-10 lg:grid-cols-2 lg:gap-14">
          <div className="flex flex-col justify-center">
            <span className="eyebrow">
              <span className="gold-rule" aria-hidden="true" />
              {v.eyebrow}
            </span>
            <Reveal>
              <h2 className="font-display mt-4 text-3xl leading-tight text-primary sm:text-4xl">{v.title}</h2>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                    <MapPin className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-display text-lg text-primary">{v.name}</p>
                    <p className="text-sm text-muted-foreground">{v.institute}</p>
                    <p className="mt-1.5 max-w-md text-sm leading-relaxed text-muted-foreground">{v.address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                    <Phone className="h-5 w-5" />
                  </span>
                  <div className="flex flex-col gap-0.5">
                    {PHONES.map((phone) => (
                      <a key={phone} href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="text-sm font-medium text-foreground transition-colors hover:text-gold-ink">
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                    <Globe className="h-5 w-5" />
                  </span>
                  <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-foreground transition-colors hover:text-gold-ink">
                    {WEBSITE_LABEL}
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="mt-8 rounded-xl border border-border bg-secondary/50 p-5">
                <p className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-gold-ink">{v.venuesTitle}</p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-card text-teal shadow-sm">
                      <Presentation className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-primary">{v.general}</p>
                      <p className="text-xs leading-relaxed text-muted-foreground">{v.generalPlace}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-card text-teal shadow-sm">
                      <Wrench className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-primary">{v.handson}</p>
                      <p className="text-xs leading-relaxed text-muted-foreground">{v.handsonPlace}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild variant="default" size="lg">
                  <a href={BROCHURE_URL} target="_blank" rel="noopener noreferrer" download>
                    <FileText className="h-4 w-4" /> {t.hero.brochure}
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
                    {v.cta} <ArrowUpRight className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.08} className="relative">
            <div className="relative h-full min-h-[20rem] overflow-hidden rounded-2xl shadow-elevated">
              <Parallax distance={44} className="h-full w-full">
                <img src={IMAGES.campus} alt="Institute of Engineering & Management campus" className="h-[112%] w-full object-cover" loading="lazy" />
              </Parallax>
              <div className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, hsl(var(--navy-deep) / 0.72) 100%)" }} />
              <div className="absolute bottom-0 left-0 p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-soft">{t.hero.dates}</p>
                <p className="font-display mt-1 text-xl text-primary-foreground">{v.captionInstitute}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
